import React from 'react';
import { Icon, Label, Stack, Toggle } from '@fluentui/react';

// Patterns that must never appear in merchant-authored config that renders to shoppers:
// script URLs, IE expressions, @import, external url() references, and raw HTML.
const DANGEROUS = /(expression\s*\(|javascript\s*:|@import|url\s*\(|<|>)/i;

// The result shape this editor renders, and the fixed contract a caller-supplied linter returns.
// `warnings` is optional because the built-in linter never produces any: it is either valid or it
// is refused. Richer linters (the Search page config lint) use warnings for "this parses and is
// applied, but you probably didn't mean it".
export interface IRawLint {
    valid: boolean;
    errors: string[];
    warnings?: string[];
    parsed?: unknown;
}

function lintRawJson(raw: string): IRawLint {
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch (e) {
        return { valid: false, errors: [`Invalid JSON: ${(e as Error).message}`] };
    }
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { valid: false, errors: ['The top-level value must be a JSON object.'] };
    }
    if (DANGEROUS.test(raw)) {
        return { valid: false, errors: ['Disallowed content (url(), @import, javascript:, or angle brackets) is not permitted.'] };
    }
    return { valid: true, errors: [], parsed };
}

interface IRawJsonEditorProps {
    label: string;
    // Parsed config from the form state — seeds the textarea when entering raw mode.
    liveObject: unknown;
    // Called with a serialized JSON string while the text is valid (applies to the preview).
    onApply: (json: string) => void;
    keysHint?: string;
    /**
     * Optional schema-aware linter that fully REPLACES the built-in lintRawJson (it is not layered
     * on top of it), so a caller that supplies one owns the dangerous-content check too: nothing
     * else screens the text before it reaches onApply. In exchange it can validate keys, flag
     * unknown properties as warnings, and hand back an already-typed `parsed`.
     */
    lint?: (raw: string) => IRawLint;
    // Form controls, shown when not in raw mode.
    children?: React.ReactNode;
}

// Reusable "Raw JSON" editing affordance: a header row with a Raw JSON toggle; the form
// controls show when off, a validated textarea when on. Applies to the live config while
// the JSON parses cleanly (mirrors the Search page editor's raw mode).
//
// Validation is pluggable: without a `lint` prop it is the built-in parse + dangerous-content
// screen above; with one, that linter replaces it entirely and can additionally surface amber
// warnings, which annotate an applied edit rather than blocking it.
const RawJsonEditor: React.FC<IRawJsonEditorProps> = ({ label, liveObject, onApply, keysHint, lint: customLint, children }) => {
    const [rawMode, setRawMode] = React.useState(false);
    const [rawText, setRawText] = React.useState('');
    // `result` (not `lint`) so the state doesn't shadow the linter prop.
    const [result, setResult] = React.useState<IRawLint | null>(null);

    // One entry point for both the seed and every keystroke, so a caller's linter is the ONLY
    // linter that ever runs when one is supplied.
    const runLint = (text: string): IRawLint => (customLint ? customLint(text) : lintRawJson(text));
    const warnings = result?.warnings ?? [];

    const enterRaw = () => {
        const text = JSON.stringify(liveObject ?? {}, null, 2);
        setRawText(text);
        setResult(runLint(text));
        setRawMode(true);
    };
    const onRawChange = (text: string) => {
        setRawText(text);
        const lintResult = runLint(text);
        setResult(lintResult);
        if (lintResult.valid) onApply(JSON.stringify(lintResult.parsed));
    };

    return (
        <div style={{ marginBottom: '1em' }}>
            <Stack horizontal horizontalAlign='space-between' verticalAlign='center' style={{ marginBottom: 8 }}>
                <Label>{label}</Label>
                <Toggle inlineLabel label='Raw JSON' checked={rawMode} onChange={(_, checked) => (checked ? enterRaw() : setRawMode(false))} styles={{ root: { marginBottom: 0 } }} />
            </Stack>
            {rawMode ? (
                <div style={{ padding: '1em', border: '1px solid #edebe9', borderRadius: 4, background: '#faf9f8' }}>
                    <textarea
                        value={rawText}
                        onChange={(e) => onRawChange(e.target.value)}
                        spellCheck={false}
                        aria-label={`${label} JSON`}
                        style={{ width: '100%', minHeight: 220, boxSizing: 'border-box', fontFamily: 'Consolas, "Courier New", monospace', fontSize: 13, lineHeight: 1.5, padding: 8, borderRadius: 4, resize: 'vertical', border: `1px solid ${result && !result.valid ? '#d13438' : '#8a8886'}` }}
                    />
                    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {result?.errors.map((e, i) => (
                            <div key={`e${i}`} style={{ color: '#a4262c', fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
                                <Icon iconName='ErrorBadge' /><span>{e}</span>
                            </div>
                        ))}
                        {/* Amber, between the errors and the green line: warnings do NOT block the
                            apply, so the "Valid" confirmation stands down for them and the amber
                            lines carry the message on their own. */}
                        {warnings.map((w, i) => (
                            <div key={`w${i}`} style={{ color: '#8a6d00', fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
                                <Icon iconName='Warning' /><span>{w}</span>
                            </div>
                        ))}
                        {result?.valid && warnings.length === 0 && (
                            <div style={{ color: '#107c10', fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
                                <Icon iconName='CompletedSolid' /><span>Valid — applied to the preview.</span>
                            </div>
                        )}
                    </div>
                    {keysHint && <div style={{ marginTop: 6, fontSize: 11, color: '#605e5c' }}>{keysHint}</div>}
                </div>
            ) : children}
        </div>
    );
};

export default RawJsonEditor;
